import {
  Component,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { FrameProvider } from './frame'
import type { TutorialDef } from './types'
import s from './TutorialPlayer.module.css'

/* Plays a tutorial film: a fixed-size canvas (def.width × def.height) scaled
   to its box, driven by a requestAnimationFrame clock at def.fps. It pauses
   off screen and in a hidden tab, holds the poster frame under reduced motion
   until Play is pressed, and never shifts layout while the film's code loads.
   The mini loops def.mini's frames when the film has them, else the whole film.

   The film keeps its light "video" palette in both themes (the canvas
   re-declares the light tokens); the controls around it follow the theme. */

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'
/** The mini player draws at 30 fps: every other frame of a 60 fps film. */
const MINI_FPS = 30
const SEEK_SECONDS = 5

export interface TutorialPlayerProps {
  tutorial: TutorialDef
  /** full: controls under the film. mini: a silent loop with no controls (peek tab, cards). */
  variant?: 'full' | 'mini'
  /** Default true for mini, false for full. Ignored under reduced motion. */
  autoPlay?: boolean
  /** Default true for mini, false for full. */
  loop?: boolean
  /** Each time the film reaches its last frame (every pass when looping). */
  onEnded?: () => void
  /** Once, the first time the film actually starts moving. */
  onFirstPlay?: () => void
  className?: string
}

/* --- Film canvas ------------------------------------------------------------ */

class FilmBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  componentDidCatch(error: unknown) {
    console.error('Tutorial film failed to render', error)
  }
  render() {
    return this.state.failed ? null : this.props.children
  }
}

/** Scale that fits `width` film pixels into the viewport's layout width
    (untransformed, so a sliding or tilted parent doesn't skew it). */
function useFitScale(width: number) {
  const ref = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(0)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const fit = (box: number) => {
      if (box > 0) setScale(box / width)
    }
    fit(Number.parseFloat(getComputedStyle(el).width) || el.clientWidth)
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => fit(entry.contentRect.width))
    observer.observe(el)
    return () => observer.disconnect()
  }, [width])
  return { ref, scale }
}

function FilmCanvas({ tutorial, frame, compact }: { tutorial: TutorialDef; frame: number; compact: boolean }) {
  const { ref, scale } = useFitScale(tutorial.width)
  const Composition = tutorial.Composition
  const style: CSSProperties = {
    width: tutorial.width,
    height: tutorial.height,
    transform: `scale(${scale || 1})`,
    visibility: scale ? undefined : 'hidden',
  }
  return (
    <div ref={ref} className={s.viewport} aria-hidden="true">
      <div className={s.canvas} style={style} data-film-canvas={tutorial.id} data-frame={frame}>
        <FilmBoundary>
          <Suspense fallback={null}>
            <FrameProvider
              frame={frame}
              fps={tutorial.fps}
              width={tutorial.width}
              height={tutorial.height}
              durationInFrames={tutorial.durationInFrames}
            >
              <Composition compact={compact} />
            </FrameProvider>
          </Suspense>
        </FilmBoundary>
      </div>
    </div>
  )
}

const aspect = (tutorial: TutorialDef) => `${tutorial.width} / ${tutorial.height}`

/** One frame of a film, scaled to its box: posters, thumbnails, screenshots. */
export function TutorialStill({
  tutorial,
  frame = tutorial.posterFrame,
  compact = false,
  className,
}: {
  tutorial: TutorialDef
  frame?: number
  compact?: boolean
  className?: string
}) {
  return (
    <div className={`${s.stage} ${className ?? ''}`.trim()} data-variant="still" style={{ aspectRatio: aspect(tutorial) }}>
      <FilmCanvas tutorial={tutorial} frame={frame} compact={compact} />
    </div>
  )
}

/* --- Clock ------------------------------------------------------------------ */

interface PlaybackOptions {
  autoPlay: boolean
  loop: boolean
  /** Frames advanced per drawn frame (2 for the 30 fps mini). */
  step: number
  /** The frames the clock plays, [from, to): the whole film, or the mini's loop. */
  from: number
  to: number
  onEnded?: () => void
  onFirstPlay?: () => void
}

function usePlayback(tutorial: TutorialDef, root: RefObject<HTMLElement>, options: PlaybackOptions) {
  const { loop, step, from, to } = options
  const reduced = useMediaQuery(REDUCED_MOTION)
  const autoplays = options.autoPlay && !reduced
  const last = Math.max(from, to - 1)

  const [playing, setPlaying] = useState(autoplays)
  const [started, setStarted] = useState(autoplays)
  const [ended, setEnded] = useState(false)
  const [frame, setFrame] = useState(from)
  const [onScreen, setOnScreen] = useState(true)
  const [tabHidden, setTabHidden] = useState(() => typeof document !== 'undefined' && document.hidden)
  const position = useRef(from)
  const userDriven = useRef(false)
  const firstPlayed = useRef(false)
  const callbacks = useRef({ onEnded: options.onEnded, onFirstPlay: options.onFirstPlay })
  useLayoutEffect(() => {
    callbacks.current = { onEnded: options.onEnded, onFirstPlay: options.onFirstPlay }
  })

  // Until someone presses a control, follow autoPlay and reduced motion as they change.
  useEffect(() => {
    if (userDriven.current) return
    setPlaying(autoplays)
    setStarted(autoplays)
    setEnded(false)
    position.current = from
    setFrame(from)
  }, [autoplays, from])

  useEffect(() => {
    const el = root.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(([entry]) => setOnScreen(entry?.isIntersecting ?? true), { threshold: 0.15 })
    observer.observe(el)
    return () => observer.disconnect()
  }, [root])

  useEffect(() => {
    const sync = () => setTabHidden(document.hidden)
    document.addEventListener('visibilitychange', sync)
    return () => document.removeEventListener('visibilitychange', sync)
  }, [])

  const running = playing && onScreen && !tabHidden
  useEffect(() => {
    if (!running) return
    if (!firstPlayed.current) {
      firstPlayed.current = true
      callbacks.current.onFirstPlay?.()
    }
    const perMs = tutorial.fps / 1000
    let handle = 0
    let previous: number | null = null
    let shown = -1
    const tick = (now: number) => {
      // Cap each step so a stalled tab or a long GC doesn't jump the film.
      if (previous !== null) position.current += Math.min(now - previous, 100) * perMs
      previous = now
      if (position.current >= to) {
        if (loop) {
          position.current = from + ((position.current - to) % Math.max(1, to - from))
          callbacks.current.onEnded?.()
        } else {
          position.current = last
          setFrame(last)
          setPlaying(false)
          setEnded(true)
          callbacks.current.onEnded?.()
          return
        }
      }
      const next = Math.min(last, Math.floor(position.current / step) * step)
      if (next !== shown) {
        shown = next
        setFrame(next)
      }
      handle = requestAnimationFrame(tick)
    }
    handle = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(handle)
  }, [running, loop, step, from, to, last, tutorial.fps])

  const seek = useCallback(
    (target: number) => {
      userDriven.current = true
      const f = Math.max(from, Math.min(last, Math.round(target)))
      position.current = f
      setFrame(f)
      setStarted(true)
      setEnded(false)
    },
    [from, last],
  )

  /** Seek and keep playing from there (a chapter). */
  const jump = useCallback(
    (target: number) => {
      seek(target)
      setPlaying(true)
    },
    [seek],
  )

  const play = useCallback(() => {
    userDriven.current = true
    if (!started || ended || position.current >= last) {
      position.current = from
      setFrame(from)
    }
    setStarted(true)
    setEnded(false)
    setPlaying(true)
  }, [started, ended, from, last])

  const pause = useCallback(() => {
    userDriven.current = true
    setPlaying(false)
  }, [])

  const replay = useCallback(() => {
    userDriven.current = true
    position.current = from
    setFrame(from)
    setStarted(true)
    setEnded(false)
    setPlaying(true)
  }, [from])

  const toggle = useCallback(() => (playing ? pause() : play()), [playing, pause, play])

  return {
    /** The frame on screen: the poster until the film first starts. */
    frame: started ? frame : tutorial.posterFrame,
    playing,
    started,
    ended,
    reduced,
    play,
    pause,
    toggle,
    replay,
    seek,
    jump,
    /** Pause while the scrubber is held; returns a function that resumes. */
    hold: () => {
      const was = playing
      if (was) setPlaying(false)
      return () => {
        if (was) setPlaying(true)
      }
    },
  }
}

/* --- Controls --------------------------------------------------------------- */

function clock(frames: number, fps: number) {
  const seconds = Math.max(0, Math.floor(frames / fps))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

function chapterAt(tutorial: TutorialDef, frame: number) {
  const chapters = tutorial.chapters ?? []
  let index = -1
  chapters.forEach((chapter, i) => {
    if (chapter.from <= frame) index = i
  })
  return index >= 0 ? { index, label: chapters[index].label } : null
}

function PlayGlyph() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <path d="M7.1 4.6Q5.8 3.8 5.8 5.3V14.7Q5.8 16.2 7.1 15.4L14.9 10.8Q16.1 10 14.9 9.2Z" fill="currentColor" />
    </svg>
  )
}

function PauseGlyph() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <path d="M7 5.5V14.5M13 5.5V14.5" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" fill="none" />
    </svg>
  )
}

function ReplayGlyph() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <path
        d="M4.6 8.2A5.9 5.9 0 1 1 4.3 12M4.4 3.9V8.3H8.8"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  )
}

/* --- Player ----------------------------------------------------------------- */

/** Keyed by tutorial, so switching films starts the new one fresh. */
export function TutorialPlayer(props: TutorialPlayerProps) {
  return <Player key={props.tutorial.id} {...props} />
}

function Player({
  tutorial,
  variant = 'full',
  autoPlay,
  loop,
  onEnded,
  onFirstPlay,
  className,
}: TutorialPlayerProps) {
  const mini = variant === 'mini'
  const root = useRef<HTMLDivElement>(null)
  const stage = useRef<HTMLDivElement>(null)
  const range = mini && tutorial.mini ? tutorial.mini : { from: 0, to: tutorial.durationInFrames }
  const playback = usePlayback(tutorial, root, {
    autoPlay: autoPlay ?? mini,
    loop: loop ?? mini,
    step: mini ? Math.max(1, Math.round(tutorial.fps / MINI_FPS)) : 1,
    from: range.from,
    to: range.to,
    onEnded,
    onFirstPlay,
  })
  const last = Math.max(0, tutorial.durationInFrames - 1)
  const { frame } = playback

  if (mini) {
    return (
      <div ref={root} className={`${s.player} ${className ?? ''}`.trim()} data-variant="mini" aria-hidden="true">
        <div className={s.stage} data-variant="mini" style={{ aspectRatio: aspect(tutorial) }}>
          <FilmCanvas tutorial={tutorial} frame={frame} compact />
        </div>
      </div>
    )
  }

  // Before the first play the stage shows the poster, but the controls read
  // from the start: Play begins at 0:00.
  const at = playback.started ? frame : 0
  const chapters = tutorial.chapters?.length ? tutorial.chapters : [{ label: '', from: 0 }]
  const chapter = chapterAt(tutorial, at)
  const elapsed = clock(at, tutorial.fps)
  const total = clock(tutorial.durationInFrames, tutorial.fps)
  const fraction = last > 0 ? at / last : 0
  const showCue = !playback.playing && (!playback.started || playback.ended)

  function seekBy(frames: number) {
    playback.seek(at + frames)
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.altKey || event.ctrlKey || event.metaKey) return
    const target = event.target as HTMLElement
    const onButton = target.tagName === 'BUTTON'
    const step = SEEK_SECONDS * tutorial.fps
    switch (event.key) {
      case ' ':
      case 'Spacebar':
        if (onButton) return // the button's own click handles it
        playback.toggle()
        break
      case 'k':
      case 'K':
        playback.toggle()
        break
      case 'ArrowLeft':
        seekBy(-step)
        break
      case 'ArrowRight':
        seekBy(step)
        break
      case 'ArrowDown':
        seekBy(-tutorial.fps)
        break
      case 'ArrowUp':
        seekBy(tutorial.fps)
        break
      case 'Home':
        playback.seek(0)
        break
      case 'End':
        playback.seek(last)
        break
      default:
        return
    }
    event.preventDefault()
  }

  return (
    <div
      ref={root}
      className={`${s.player} ${className ?? ''}`.trim()}
      data-variant="full"
      data-playing={playback.playing}
      role="group"
      aria-label={`${tutorial.title} video`}
      onKeyDown={onKeyDown}
    >
      <div
        ref={stage}
        className={s.stage}
        data-variant="full"
        style={{ aspectRatio: aspect(tutorial) }}
        tabIndex={-1}
        onClick={() => {
          playback.toggle()
          stage.current?.focus({ preventScroll: true })
        }}
      >
        <FilmCanvas tutorial={tutorial} frame={frame} compact={false} />
        {showCue && (
          <span className={s.cue} data-ended={playback.ended} aria-hidden="true">
            {playback.ended ? <ReplayGlyph /> : <PlayGlyph />}
          </span>
        )}
      </div>

      <div className={s.controls}>
        <div className={s.scrub}>
          <div className={s.track} aria-hidden="true">
            {chapters.map((c, i) => {
              const end = i + 1 < chapters.length ? chapters[i + 1].from : tutorial.durationInFrames
              const start = c.from / tutorial.durationInFrames
              const size = (end - c.from) / tutorial.durationInFrames
              const filled = Math.max(0, Math.min(1, (at - c.from) / Math.max(1, end - c.from)))
              // A 3px break between chapters.
              const before = i > 0 ? 1.5 : 0
              const after = i + 1 < chapters.length ? 1.5 : 0
              return (
                <span
                  key={c.from}
                  className={s.segment}
                  data-current={chapter?.index === i}
                  style={{ left: `calc(${start * 100}% + ${before}px)`, width: `calc(${size * 100}% - ${before + after}px)` }}
                >
                  <span className={s.fill} style={{ transform: `scaleX(${filled})` }} />
                </span>
              )
            })}
          </div>
          <span className={s.thumb} style={{ left: `${fraction * 100}%` }} aria-hidden="true" />
          <input
            type="range"
            className={s.range}
            min={0}
            max={last}
            step={1}
            value={at}
            aria-label="Seek"
            aria-valuetext={`${elapsed} of ${total}${chapter?.label ? `, ${chapter.label}` : ''}`}
            onChange={(event) => playback.seek(Number(event.currentTarget.value))}
            onPointerDown={() => {
              const resume = playback.hold()
              const release = () => {
                resume()
                window.removeEventListener('pointerup', release)
                window.removeEventListener('pointercancel', release)
              }
              window.addEventListener('pointerup', release)
              window.addEventListener('pointercancel', release)
            }}
          />
        </div>
        <div className={s.bar}>
          <button
            type="button"
            className={s.playButton}
            onClick={playback.toggle}
            aria-label={playback.playing ? 'Pause' : playback.ended ? 'Play again' : 'Play'}
          >
            {playback.playing ? <PauseGlyph /> : <PlayGlyph />}
          </button>
          {/* The live region and the scrubber's value text carry these for screen readers. */}
          <span className={s.chapter} aria-hidden="true">
            {chapter?.label}
          </span>
          <span className={s.time} aria-hidden="true">
            <span className={s.elapsed}>{elapsed}</span>
            <span className={s.timeDivider}>/</span>
            {total}
          </span>
          <button type="button" className={s.iconButton} onClick={playback.replay} aria-label="Replay from the start">
            <ReplayGlyph />
          </button>
        </div>
        {chapters.length > 1 && (
          <ol className={s.chapters} aria-label="Chapters">
            {chapters.map((c, i) => (
              <li key={c.from}>
                <button type="button" aria-current={chapter?.index === i || undefined} onClick={() => playback.jump(c.from)}>
                  {c.label}
                </button>
              </li>
            ))}
          </ol>
        )}
        <p className={s.live} aria-live="polite" aria-atomic="true">
          {chapter?.label ?? ''}
        </p>
      </div>
    </div>
  )
}
